import { ReactNode, useState } from 'react';
import { BsBookmarkCheckFill, BsBookmarkPlus } from 'react-icons/bs';

import { ActionIcon, ActionIconProps, Button, ButtonProps, Tooltip } from '@mantine/core';

import { CompanyBase, CompanyProfile, QrScanSource, QrScanType } from 'models';
import { QrScanHelpers } from 'helpers/QrScanHelpers';

import { SaveScanFlow } from 'components/showtime/components/SaveScanFlow';
import { useScanEvent } from 'components/showtime/hooks/use-scan-event';
import { useSavedProfiles } from 'components/showtime/hooks/useSavedProfiles';
import { openScanSheet } from 'components/showtime/state/scan-sheet';
import { useCompanyProfile } from 'hooks/use-company-profile';
import { InternalTrackingService } from 'services/InternalTrackingService';

type ShowtimeButtonProps = {
    company: CompanyBase | CompanyBase['id'] | null | undefined;
    type: QrScanType;
    itemId: string;
    title: string;
    iconOnly?: boolean;
    buttonProps?: ButtonProps;
    actionIconProps?: ActionIconProps;
    // Shown outside events, e.g. the regular save button, so the page never shows two bookmark buttons
    fallback?: ReactNode;
};

// The one "Save to RE+ Showtime" button for profiles, brochures and products, so saving works the same everywhere. It
// only shows while the visitor or the exhibitor is at an event, since the list is the visitor's list for that event.
// Saving and reopening both end in the app's scan notes sheet.
const ShowtimeButton = (props: ShowtimeButtonProps) => {
    const { company, isLoading: companyLoading } = useCompanyProfile(props.company);
    const { event, isLoading: eventLoading } = useScanEvent({ id: company?.id ?? '' });

    if (companyLoading || eventLoading) {
        return null;
    }

    if (!company || !event) {
        return props.fallback ?? null;
    }

    return <ActiveShowtimeButton key={props.itemId} {...props} company={company} />;
};

const ActiveShowtimeButton = ({
    company,
    type,
    itemId,
    title,
    iconOnly,
    buttonProps,
    actionIconProps,
}: ShowtimeButtonProps & { company: CompanyProfile }) => {
    const { qrScans } = useSavedProfiles();
    const savedScan = QrScanHelpers.findLatestScan(qrScans, type, itemId);

    const [saving, setSaving] = useState(false);

    const startSaving = () => {
        InternalTrackingService.track('qrScan.view.bookmark', { type, item: itemId, company: company.id });
        setSaving(true);
    };

    const onClick = () => (savedScan ? openScanSheet({ company, itemId, scan: savedScan }) : startSaving());
    const label = savedScan ? 'Saved to RE+ Showtime' : 'Save to RE+ Showtime';
    const icon = savedScan ? <BsBookmarkCheckFill /> : <BsBookmarkPlus />;

    return (
        <>
            {iconOnly ? (
                <Tooltip label={label}>
                    <ActionIcon
                        variant="white"
                        size="lg"
                        radius={99}
                        c={savedScan ? 'green' : undefined}
                        aria-label={label}
                        onClick={onClick}
                        {...actionIconProps}
                    >
                        {icon}
                    </ActionIcon>
                </Tooltip>
            ) : (
                <Tooltip label={label}>
                    <Button
                        leftSection={icon}
                        variant="white"
                        color={savedScan ? 'green' : 'dark'}
                        c={savedScan ? 'green' : undefined}
                        onClick={onClick}
                        {...buttonProps}
                    >
                        {savedScan ? 'Saved' : 'Save'}
                    </Button>
                </Tooltip>
            )}

            {saving && (
                <SaveScanFlow
                    company={company}
                    type={type}
                    itemId={itemId}
                    title={title}
                    source={QrScanSource.WEB}
                    onScanSaving={(saving) => {
                        setSaving(false);
                        openScanSheet({ company, itemId, ...saving });
                    }}
                    onCancel={() => setSaving(false)}
                />
            )}
        </>
    );
};

export { ShowtimeButton };
